/**
 * Settings Page
 * User profile and security settings for authenticated users
 */

import { useState, useEffect } from 'react'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'
import { useProfile, useUpdateProfile, useChangePassword } from '@/hooks/use-profile'
import { useAuthStore } from '@/stores/auth-store'
import type { UpdateProfileRequest, ChangePasswordRequest } from '@/types/user-management'

// ============================================================================
// HELPERS
// ============================================================================

function validateStrongPassword(pw: string): string | null {
  if (pw.length < 8) return 'At least 8 characters required'
  if (!/[A-Z]/.test(pw)) return 'Must contain uppercase letter'
  if (!/[a-z]/.test(pw)) return 'Must contain lowercase letter'
  if (!/[0-9]/.test(pw)) return 'Must contain a number'
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Must contain a special character'
  return null
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function SettingsPage() {
  const { data: profile } = useProfile()
  const authUser = useAuthStore((state) => state.user)
  const updateProfile = useUpdateProfile()
  const changePassword = useChangePassword()

  const mustChangePassword = authUser?.mustChangePassword ?? false

  // Profile form state
  const [displayName, setDisplayName] = useState('')
  const [avatarUrl, setAvatarUrl] = useState('')
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({})

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({})

  // Sync profile form with fetched data
  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName)
      setAvatarUrl(profile.avatarUrl ?? '')
    } else if (authUser) {
      setDisplayName(authUser.displayName)
    }
  }, [profile, authUser])

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const errors: Record<string, string> = {}
    if (!displayName.trim()) errors.displayName = 'Display name is required'
    if (avatarUrl && !/^https?:\/\/.+/.test(avatarUrl)) errors.avatarUrl = 'Must be a valid URL'
    setProfileErrors(errors)
    if (Object.keys(errors).length > 0) return

    const request: UpdateProfileRequest = { displayName: displayName.trim() }
    if (avatarUrl) request.avatarUrl = avatarUrl
    updateProfile.mutate(request)
  }

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const errors: Record<string, string> = {}
    if (!currentPassword) errors.currentPassword = 'Current password is required'
    const pwErr = validateStrongPassword(newPassword)
    if (pwErr) errors.newPassword = pwErr
    if (newPassword !== confirmPassword) errors.confirmPassword = 'Passwords do not match'
    setPasswordErrors(errors)
    if (Object.keys(errors).length > 0) return

    const request: ChangePasswordRequest = { currentPassword, newPassword }
    changePassword.mutate(request, {
      onSuccess: () => {
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        setPasswordErrors({})
      },
    })
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="flex items-center gap-2 px-4 py-3 border-b">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="h-4" />
        <h1 className="text-sm font-medium">Settings</h1>
      </header>

      <div className="flex-1 overflow-auto p-6 space-y-6 max-w-2xl">
        {/* Force password change banner */}
        {mustChangePassword && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Password Change Required</AlertTitle>
            <AlertDescription>
              You must change your password before continuing. Please use the form below.
            </AlertDescription>
          </Alert>
        )}

        {/* Profile */}
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
            <CardDescription>Update your display name and avatar URL.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  placeholder="Your name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
                {profileErrors.displayName && (
                  <p className="text-xs text-destructive">{profileErrors.displayName}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="avatarUrl">Avatar URL (optional)</Label>
                <Input
                  id="avatarUrl"
                  placeholder="https://example.com/avatar.png"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                />
                {profileErrors.avatarUrl && (
                  <p className="text-xs text-destructive">{profileErrors.avatarUrl}</p>
                )}
              </div>

              <Button type="submit" disabled={updateProfile.isPending}>
                {updateProfile.isPending ? 'Saving...' : 'Save Profile'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle>Change Password</CardTitle>
            <CardDescription>
              Password must be at least 8 characters and include uppercase, lowercase, number, and special character.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  placeholder="Your current password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
                {passwordErrors.currentPassword && (
                  <p className="text-xs text-destructive">{passwordErrors.currentPassword}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                {passwordErrors.newPassword && (
                  <p className="text-xs text-destructive">{passwordErrors.newPassword}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                {passwordErrors.confirmPassword && (
                  <p className="text-xs text-destructive">{passwordErrors.confirmPassword}</p>
                )}
              </div>

              <Button type="submit" disabled={changePassword.isPending}>
                {changePassword.isPending ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
